
import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import ApiClient from "@/utilities/ApiClient";
import FileDownload from "@/utilities/FileDownload";
import { Resource } from '@/model/Resource';
import { IFileUploadResponse } from "@/model/IFileUploadResponse";
import * as toastr from "toastr";
import Utils from "@/utilities/Utils";

//@Component({ inject: ["form"] })
@Component({ inject: { form: { from: "form", default: () => { console.warn("PhotoCapture - Form not injected!"); } } } })
export default class PhotoCapture extends Vue {

    created(): void {
        // for validation
        // from https://stackoverflow.com/questions/65410355
        // https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/components/VInput/VInput.ts
        const form = (this as any).form;
        if(form) form.register(this);
    }

    mounted(): void {
        console.log("PhotoCapture MOUNTED - canvas = ",  this.$refs.canvas);
        console.log("PhotoCapture MOUNTED - cameraContainer = ",  this.$refs.cameraContainer);

        const cameraContainer = this.$refs.cameraContainer as HTMLElement;
        const resizeObserver = new ResizeObserver(entries => {
            const padding = 20;
            this.photoWidth = cameraContainer.getBoundingClientRect().width - padding;
        });
        resizeObserver.observe(cameraContainer);
    }

    //
    // -- props and data
    //

    @Prop({ required: true }) resource!: Resource;
    @Prop({ required: true }) label!: string;
    @Prop({ default: () => [] }) rules!: Array<(() => string | boolean) | ((v: any) => string | true)>;
    isUploading: boolean = false;
    otherErrorText: string = "";
    errorCssClass: string = "";
    validationErrorText: string = "";
    isExistingImage: boolean = false;

    showExistingImage(resource: Resource): void {
        if(!resource.hasFile) {
            console.log("PHOTO - no existing image");
            this.isExistingImage = false;
            return;
        }
        const existingImage = this.$refs.existingImage as HTMLImageElement;
        existingImage.src = resource.sourceUrl ?? require("@/images/missing.png");
        console.log("PHOTO - existing image URL = ", resource.sourceUrl);
        this.isExistingImage = true;
    }

    //
    // -- validation
    //

    didValidate: boolean = false; // for lazy validation

    resetValidation(): void {
        this.validationErrorText = "";
        this.errorCssClass = "";
        this.didValidate = false;
    }

    validate(isForce = false): boolean {
        let isValid = true;
        let validationErrorText = "";
        this.rules.forEach(rule => {
            const ruleResult = rule(this.resource);
            const isOk = (typeof ruleResult === "boolean" && ruleResult);
            if(!isOk) {
                isValid = false;
                if(typeof ruleResult === "string") {
                    validationErrorText += validationErrorText === "" ? ruleResult : ` | ${ruleResult}`;
                }
            }
        });
//console.log(`** validated isValid: ${isValid}  message: ${validationErrorText}`)
        this.validationErrorText = validationErrorText;
        this.errorCssClass = isValid ? "" : "has-error";
        this.didValidate = true;
        return isValid;
    }

    //
    // -- camera stuff
    //

    photoWidth:number = 300;
    get photoHeight(): number {
        return this.photoWidth * 2.0 / 3.0;
    }

    isCameraOpen: boolean = false;
    isPhotoTaken: boolean = false;
    isFlashEffect: boolean = false;
    isLoading: boolean = false;
    link: string = "#";

    toggleCamera(): void {
        if(this.isCameraOpen) {
            this.isCameraOpen = false;
            this.isPhotoTaken = false;
            this.isFlashEffect = false;
            this.stopCameraStream();
        } else {
            this.isCameraOpen = true;
            this.createCameraElement();
        }
    }

    async reset(): Promise<void> {
        this.isCameraOpen = false;
        this.isPhotoTaken = false;
        this.isFlashEffect = false;
        this.stopCameraStream();
        this.otherErrorText = "";
        Utils.resetObject(this.resource);
    }

    createCameraElement(): void {
        this.isLoading = true;
        navigator.mediaDevices
            .getUserMedia({ audio: false, video: true }) // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
            .then(stream => {
                this.isLoading = false;
                const video = this.$refs.camera as HTMLVideoElement;
                video.srcObject = stream;
                this.otherErrorText = "";
            })
            .catch(error => {
                this.isLoading = false;
                this.otherErrorText = "Unable to open camera - is it already in use by another app?"
                console.warn("Failed to open camera: ", error);

                this.isCameraOpen = false;
            });
    }

    stopCameraStream(): void {
        const video = this.$refs.camera as HTMLVideoElement;
        const stream = video.srcObject as MediaStream;
        const tracks = stream.getTracks();
        tracks.forEach(track => {
            track.stop();
        });
    }

    takePhoto(): void {
        if(!this.isPhotoTaken) {
            this.isFlashEffect = true;
            setTimeout(() => {
                this.isFlashEffect = false;
            }, 70);
        }
        this.isPhotoTaken = !this.isPhotoTaken;

        if(this.isPhotoTaken) {
            const canvas = this.$refs.canvas as HTMLCanvasElement;
            const context = canvas.getContext("2d");
            context?.drawImage((this.$refs.camera as HTMLVideoElement), 0, 0, this.photoWidth, this.photoHeight);
            // start the upload
            canvas.toBlob(this.uploadBlob, "image/jpeg", 0.8);
        }
    }

    //
    // -- server api
    //

    async uploadBlob(blob: Blob|null): Promise<void> {
        if(blob == null) return;

        const formData = new FormData();
        formData.append("formFile", blob, "visit-reg-photo.jpg");
        //formData.append("someID", "666");
        this.isUploading = true;
        this.otherErrorText = "";
        try {
            const response: IFileUploadResponse = await ApiClient.postNonJson("/api/file/uploadPublic", formData);

//console.log("@@@ uploadPublic response ", response);

            this.resource.httpContentType = response.httpContentType;
            this.resource.originalFilename = response.originalFilename;
            this.resource.filename = response.filename;
            this.resource.size = response.size;

//console.log("@@@ uploadPublic resource ", this.resource);

            this.isUploading = false;
            if(this.didValidate) this.validate();
            toastr.success("Uploaded photo"); // ???
            this.$emit("uploaded", this.resource);
        } catch (error) {
            this.isUploading = false;
            this.otherErrorText = "Upload failed.  Please check your connection and try again.";
            toastr.error("Upload failed");
        }
    }

    // downloadImage() {
    //     const download = document.getElementById("downloadPhoto");
    //     const canvas = document.getElementById("photoTaken")?.toDataURL("image/jpeg").replace("image/jpeg", "image/octet-stream");
    //     download?.setAttribute("href", canvas);
    // }

    async download(): Promise<void> {
        FileDownload.download(this.resource);
    }
}
